/**
 * Upgrades older level-format entities to the current format (rule 11: a
 * format changes only additively or behind a `formatVersion` bump, with an
 * upgrader). Manifests, saves in this browser and clipboard copies all hold
 * entities in this format, so each runs its entities through here.
 *
 * Format 2: Mesh Renderer's `primitive` and `asset` became one `mesh`, an
 * engine shape (`engine/shapes/…`) or a model, as in Unreal and Unity.
 * Format 3: a World UI panel became a Canvas with Text children bound to the
 * content it showed (docs/planning/05-ui-authoring.md).
 * Format 4: WorldSettings' Default selection went; a level opens with nothing
 * selected, as in Unreal.
 * Format 5: fog became its own entity (a Fog component, as Unreal's height fog
 * actor) with a density per theme; WorldSettings' Fog density and the Sky's
 * Fog from horizon moved into it.
 */
import { MANIFEST_FORMAT_VERSION } from "./content";
import { contentPanelTexts } from "./contentPanel";
import type { PanelContent } from "./contentPanel";
import type { LevelManifest, ManifestEntity } from "./content";
import { ENGINE_MESHES, engineMeshPath } from "./engineAssets";
import type { EngineShape } from "./engineAssets";

/** Entities written in format `from`, as the current format writes them. */
export function upgradeEntities(
  entities: readonly ManifestEntity[],
  from: number,
): ManifestEntity[] {
  let upgraded = [...entities];
  if (from < 2) upgraded = upgraded.map(meshRendererTo2);
  if (from < 3) upgraded = worldUiTo3(upgraded);
  if (from < 4) upgraded = upgraded.map(withoutDefaultSelection);
  if (from < 5) upgraded = fogTo5(upgraded);
  return upgraded;
}

/** A manifest of any supported version, as the current format writes it. */
export function upgradeManifest(manifest: LevelManifest): LevelManifest {
  return {
    ...manifest,
    formatVersion: MANIFEST_FORMAT_VERSION,
    entities: upgradeEntities(manifest.entities, manifest.formatVersion),
  };
}

// ─── 1 → 2 ───────────────────────────────────────────────────────────────

/** Format 1's Primitive default, used when neither Primitive nor Asset was set. */
const FORMAT_1_DEFAULT_PRIMITIVE: EngineShape = "platform";

/** Props that only ever applied to the primitive; a model ignored them. */
const SHAPE_ONLY_PROPS = ["size", "material", "color"];

function meshRendererTo2(entity: ManifestEntity): ManifestEntity {
  const { components, children, ...rest } = entity;
  const old = components?.MeshRenderer as Record<string, unknown> | undefined;
  return {
    ...rest,
    ...(components && {
      components: old ? { ...components, MeshRenderer: meshTo2(old) } : components,
    }),
    ...(children && { children: children.map(meshRendererTo2) }),
  };
}

function meshTo2(old: Record<string, unknown>): Record<string, unknown> {
  const { primitive, asset, ...rest } = old;

  // The asset won over the primitive in format 1, as it does now by being the mesh.
  if (typeof asset === "string" && asset !== "") {
    const kept = Object.fromEntries(
      Object.entries(rest).filter(([key]) => !SHAPE_ONLY_PROPS.includes(key)),
    );
    return { mesh: asset, ...kept };
  }

  // An unknown primitive fell back to the default in format 1 too (with a warning).
  const shape = ENGINE_MESHES.find((mesh) => mesh.shape === primitive)?.shape;
  return { mesh: engineMeshPath(shape ?? FORMAT_1_DEFAULT_PRIMITIVE), ...rest };
}

// ─── 2 → 3 ───────────────────────────────────────────────────────────────

/** Format 2's World UI bound its parent's Role unless it said otherwise. */
const FORMAT_2_DEFAULT_BIND: PanelContent = "Role";

function worldUiTo3(entities: readonly ManifestEntity[]): ManifestEntity[] {
  const freshId = freshIn(new Set(allIds(entities)), (base, n) => `${base}-${n}`);

  const upgrade = (entity: ManifestEntity): ManifestEntity => {
    const { components, children, disabled, ...rest } = entity;
    const kids = children?.map(upgrade);
    const panel = components?.WorldUI as Record<string, unknown> | undefined;
    if (!components || !panel) {
      return {
        ...entity,
        ...(kids && { children: kids }),
      };
    }

    const { WorldUI: _old, ...others } = components;
    const content = panel.bind === "Project" ? "Project" : FORMAT_2_DEFAULT_BIND;
    const texts = contentPanelTexts(entity, content, freshId);

    return {
      ...rest,
      components: { ...others, Canvas: canvasOf(panel) },
      ...(disabled && { disabled: disabled.map((type) => (type === "WorldUI" ? "Canvas" : type)) }),
      children: [...(kids ?? []), ...texts],
    };
  };

  return entities.map(upgrade);
}

/** The panel's size (where it had one) and its look. */
function canvasOf(panel: Record<string, unknown>): Record<string, unknown> {
  return {
    ...(panel.width !== undefined && { width: panel.width }),
    ...(panel.height !== undefined && { height: panel.height }),
    background: true,
  };
}

function allIds(entities: readonly ManifestEntity[]): string[] {
  return entities.flatMap((entity) => [entity.id, ...allIds(entity.children ?? [])]);
}

function allNames(entities: readonly ManifestEntity[]): string[] {
  return entities.flatMap((entity) => [entity.name, ...allNames(entity.children ?? [])]);
}

/**
 * Makes values not yet in `taken` (ids, names): the base if it's free, else
 * the base numbered, `numbered(base, 1)` then 2 and on. Each value made is taken.
 */
function freshIn(taken: Set<string>, numbered: (base: string, n: number) => string) {
  return (base: string): string => {
    let value = base;
    for (let n = 1; taken.has(value); n++) value = numbered(base, n);
    taken.add(value);
    return value;
  };
}

// ─── 3 → 4 ───────────────────────────────────────────────────────────────

function withoutDefaultSelection(entity: ManifestEntity): ManifestEntity {
  const { components, children, ...rest } = entity;
  const settings = components?.WorldSettings as Record<string, unknown> | undefined;
  const kept =
    settings &&
    Object.fromEntries(Object.entries(settings).filter(([key]) => key !== "defaultSelection"));
  return {
    ...rest,
    ...(components && { components: kept ? { ...components, WorldSettings: kept } : components }),
    ...(children && { children: children.map(withoutDefaultSelection) }),
  };
}

// ─── 4 → 5 ───────────────────────────────────────────────────────────────

/**
 * World Settings' Fog density becomes a Fog under the level, the same for
 * day and night (a level that set none had no fog, and gets none). A Sky's
 * Fog from horizon becomes the Fog's Color from sky, which defaults on.
 */
function fogTo5(entities: readonly ManifestEntity[]): ManifestEntity[] {
  const freshId = freshIn(new Set(allIds(entities)), (base, n) => `${base}-${n}`);
  const freshName = freshIn(new Set(allNames(entities)), (base, n) => `${base}_${n}`);
  const colorFromSky = !anySkyWithoutHorizonFog(entities);

  const upgrade = (entity: ManifestEntity): ManifestEntity => {
    const { components, children, ...rest } = entity;
    const kids = children?.map(upgrade);
    if (!components) return { ...entity, ...(kids && { children: kids }) };

    const { fog, ...settings } = (components.WorldSettings ?? {}) as Record<string, unknown>;
    const { fogFromHorizon: _moved, ...sky } = (components.Sky ?? {}) as Record<string, unknown>;
    const upgraded = {
      ...components,
      ...(components.WorldSettings !== undefined && { WorldSettings: settings }),
      ...(components.Sky !== undefined && { Sky: sky }),
    };
    const fogEntity: ManifestEntity[] =
      typeof fog === "number"
        ? [
            {
              id: freshId("fog"),
              name: freshName("Fog"),
              components: {
                Fog: {
                  dayDensity: fog,
                  nightDensity: fog,
                  ...(!colorFromSky && { colorFromSky: false }),
                },
              },
            },
          ]
        : [];

    const allChildren = [...(kids ?? []), ...fogEntity];
    return {
      ...rest,
      components: upgraded,
      ...(allChildren.length > 0 && { children: allChildren }),
    };
  };

  return entities.map(upgrade);
}

function anySkyWithoutHorizonFog(entities: readonly ManifestEntity[]): boolean {
  return entities.some(
    (entity) =>
      (entity.components?.Sky as { fogFromHorizon?: unknown } | undefined)?.fogFromHorizon ===
        false || anySkyWithoutHorizonFog(entity.children ?? []),
  );
}
